"use client";

import { Check } from "lucide-react";

import { CTA_SOURCES } from "@punchup/analytics";
import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { useFollowComedian, useUserComedianFollows } from "@/hooks/follows";
import { useUser } from "@punchup/data/legacy/hooks";

/**
 * One-click subscribe CTA for comedian items on the search/discover surfaces.
 *
 * `row` matches the inline button in the search-overlay result rows; `compact`
 * is the smaller pill that sits underneath a comedian avatar/card. A logged-out
 * click routes through /signin with `comedianToFollow` so the follow completes
 * after auth (the established search-surface pattern, vs. the comedian-page
 * SubscribeButton's checkout modal).
 */
interface QuickSubscribeButtonProps {
  comedian: Pick<Comedian, "id" | "slug">;
  followLocation: FOLLOW_LOCATIONS;
  ctaSource: CTA_SOURCES;
  onNavigate: (href: string) => void;
  variant?: "row" | "compact";
}

// `compact` follows the house subscribe-CTA style — the soft peach fill with
// primary-orange label used by the feed's suggested-comedian card and the
// profile page, not a solid pill. The subscribed state keeps the same
// footprint (muted fill) so the grid doesn't reflow on click.
const BUTTON_CLASS: Record<"row" | "compact", string> = {
  row: "flex-shrink-0 rounded-lg bg-primary text-white text-xs md:text-sm font-semibold py-2 px-3.5 hover:bg-primary/90 transition-colors disabled:opacity-70",
  compact:
    "w-full rounded-md bg-[#FFEDE6] text-primary font-medium text-[11px] md:text-xs py-1.5 px-1 hover:bg-[#fad9cc] transition-colors ease-in-out duration-200 disabled:opacity-70",
};

const SUBSCRIBED_CLASS: Record<"row" | "compact", string> = {
  row: "flex items-center gap-1 text-sm font-medium text-primary flex-shrink-0",
  compact:
    "flex items-center justify-center gap-0.5 w-full rounded-md bg-gray-100 text-gray-500 font-medium text-[11px] md:text-xs py-1.5 px-1",
};

export function QuickSubscribeButton({
  comedian,
  followLocation,
  ctaSource,
  onNavigate,
  variant = "row",
}: QuickSubscribeButtonProps) {
  const user = useUser();
  const { isFollowing: followsComedian } = useUserComedianFollows();
  const isSubscribed = followsComedian(comedian.id);
  const { followComedian, isFollowing: isSubmitting } = useFollowComedian();

  const handleSubscribe = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      // /signin captures auth, auto-follows `comedianToFollow`, then returns
      // the user to the comedian's page.
      onNavigate(
        `/signin?redirectRoute=/${comedian.slug}&comedianToFollow=${comedian.slug}&followLocation=${followLocation}`
      );
      return;
    }
    if (isSubmitting) return;
    followComedian({
      comedian: { id: comedian.id },
      followLocation,
      ctaSource,
    });
  };

  if (isSubscribed) {
    return (
      <span className={SUBSCRIBED_CLASS[variant]}>
        <Check className={variant === "row" ? "h-4 w-4" : "h-3 w-3"} />
        Subscribed
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSubscribe}
      disabled={isSubmitting}
      className={BUTTON_CLASS[variant]}
    >
      Subscribe
    </button>
  );
}
